import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthStoreService } from '../services/auth-store.service';

const AUTH_ENDPOINTS_WITHOUT_REFRESH = [
  '/auth/login',
  '/auth/register',
  '/auth/refresh',
  '/auth/forgot-password',
  '/auth/reset-password',
];

// Module-level so concurrent 401s share a single in-flight refresh call (single-flight).
let refreshInFlight: Promise<string> | null = null;

function withAuthHeader(request: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  return request.clone({
    setHeaders: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      Accept: 'application/json',
    },
  });
}

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authStore = inject(AuthStoreService);
  const router = inject(Router);
  const token = authStore.token();

  const authRequest = withAuthHeader(request, token);
  const isAuthEndpointWithoutRefresh = AUTH_ENDPOINTS_WITHOUT_REFRESH.some((url) =>
    authRequest.url.includes(url),
  );

  return next(authRequest).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401 || !token || isAuthEndpointWithoutRefresh) {
        return throwError(() => error);
      }

      refreshInFlight ??= authStore.refresh(token).finally(() => {
        refreshInFlight = null;
      });

      return from(refreshInFlight).pipe(
        switchMap((newToken) => next(withAuthHeader(request, newToken))),
        catchError((refreshError: unknown) => {
          authStore.clearSession(false);
          router.navigate(['/login'], {
            queryParams: {
              redirect: router.url,
            },
          });

          return throwError(() => refreshError);
        }),
      );
    }),
  );
};
